import { useEffect, useState } from "react";
import {
  JOB_TYPE_LABELS,
  EDIT_REASON_LABELS,
  EditReason,
  type Job,
  type AuditLogEntry,
  type LoadDecision,
} from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { parseDecimal, parseDollarsToCents, MAX_TONS } from "../../lib/parse-amount";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";
import FieldError from "../../components/FieldError";
import LoadPhotosSection from "../../components/office-detail/LoadPhotosSection";
import TicketPhotoCard from "../../components/office-detail/TicketPhotoCard";
import type { OfficeRoute } from "./OfficeHome";

function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || isNaN(cents)) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function ReviewedJobDetail({
  id,
  onNavigate,
  onEdited,
}: {
  id: string;
  onNavigate: (route: OfficeRoute) => void;
  onEdited?: (jobNumber: string) => void;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [audit, setAudit] = useState<AuditLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [editing, setEditing] = useState(false);
  const [weightTons, setWeightTons] = useState("");
  const [disposalAmount, setDisposalAmount] = useState("");
  const [billableAmount, setBillableAmount] = useState("");
  const [reason, setReason] = useState<EditReason>("WEIGHT_CORRECTION");
  const [note, setNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    weightTons?: string;
    disposalAmount?: string;
    billableAmount?: string;
    reason?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Unit #26: load decision form state
  const [loadDecisionEditing, setLoadDecisionEditing] = useState(false);
  const [loadDecision, setLoadDecision] = useState<LoadDecision | "">("");
  const [loadNote, setLoadNote] = useState("");
  const [loadDecisionReason, setLoadDecisionReason] = useState<EditReason>("OTHER");
  const [loadDecisionSubmitting, setLoadDecisionSubmitting] = useState(false);
  const [loadDecisionSubmitError, setLoadDecisionSubmitError] = useState<string | null>(null);
  const [loadDecisionFieldErrors, setLoadDecisionFieldErrors] = useState<Record<string, string>>({});

  const back = () => onNavigate({ name: "home" });

  const loadJobAndAudit = () => {
    setError(null);
    Promise.all([
      apiFetch<Job>(`/api/v1/jobs/${id}`),
      apiFetch<{ audit: AuditLogEntry[] }>(`/api/v1/jobs/${id}/audit`),
    ])
      .then(([j, a]) => {
        setJob(j);
        setAudit(a.audit);
        setWeightTons(j.weightTons !== null ? String(j.weightTons) : "");
        setDisposalAmount(
          j.disposalAmountCents !== null
            ? (j.disposalAmountCents / 100).toFixed(2)
            : "",
        );
        setBillableAmount(
          j.billableAmountCents !== null
            ? (j.billableAmountCents / 100).toFixed(2)
            : "",
        );
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    loadJobAndAudit();
  }, [id]);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;
    setSubmitError(null);
    setFieldErrors({});

    const isDeliveryOnly = job.jobType === "D_O";

    const errors: Record<string, string> = {};
    if (!reason) {
      errors.reason = "Edit reason is required";
    }

    const payload: Record<string, unknown> = {
      reason,
      note: note.trim() || undefined,
    };

    if (isDeliveryOnly) {
      if (billableAmount.trim()) {
        const billCents = parseDollarsToCents(billableAmount);
        if (isNaN(billCents)) {
          errors.billableAmount = "Enter a number";
        } else if (!Number.isFinite(billCents) || !Number.isSafeInteger(billCents)) {
          errors.billableAmount = "Amount too large";
        } else {
          payload.billableAmountCents = billCents;
        }
      }
    } else {
      if (weightTons.trim()) {
        const wNum = parseDecimal(weightTons);
        if (isNaN(wNum) || wNum <= 0) {
          errors.weightTons = "Weight must be > 0";
        } else if (!Number.isFinite(wNum) || wNum > MAX_TONS) {
          errors.weightTons = "Amount too large";
        } else {
          payload.weightTons = wNum;
        }
      }

      if (disposalAmount.trim()) {
        const dispCents = parseDollarsToCents(disposalAmount);
        if (isNaN(dispCents)) {
          errors.disposalAmount = "Enter a number";
        } else if (!Number.isFinite(dispCents) || !Number.isSafeInteger(dispCents)) {
          errors.disposalAmount = "Amount too large";
        } else {
          payload.disposalAmountCents = dispCents;
        }
      }

      if (billableAmount.trim()) {
        const billCents = parseDollarsToCents(billableAmount);
        if (isNaN(billCents)) {
          errors.billableAmount = "Enter a number";
        } else if (!Number.isFinite(billCents) || !Number.isSafeInteger(billCents)) {
          errors.billableAmount = "Amount too large";
        } else {
          payload.billableAmountCents = billCents;
        }
      }

      const effectiveDisp =
        payload.disposalAmountCents !== undefined
          ? (payload.disposalAmountCents as number)
          : job.disposalAmountCents;
      const effectiveBill =
        payload.billableAmountCents !== undefined
          ? (payload.billableAmountCents as number)
          : job.billableAmountCents;

      if (
        effectiveDisp !== null &&
        effectiveDisp !== undefined &&
        effectiveBill !== null &&
        effectiveBill !== undefined &&
        effectiveBill < effectiveDisp
      ) {
        errors.billableAmount = "Billable amount must be ≥ disposal amount";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const updatedJob = await apiFetch<Job>(`/api/v1/jobs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setJob(updatedJob);
      setEditing(false);
      const freshAudit = await apiFetch<{ audit: AuditLogEntry[] }>(
        `/api/v1/jobs/${id}/audit`,
      );
      setAudit(freshAudit.audit);
      if (onEdited) onEdited(updatedJob.jobNumber);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Unit #26: submit load decision (PATCH /jobs/:id)
  const handleLoadDecisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;
    setLoadDecisionSubmitError(null);
    setLoadDecisionFieldErrors({});

    const payload: Record<string, unknown> = {
      reason: loadDecisionReason,
      note: loadNote.trim() || undefined,
    };
    if (loadDecision) payload.loadDecision = loadDecision;
    if (loadNote.trim()) payload.loadNote = loadNote.trim();

    setLoadDecisionSubmitting(true);
    try {
      const updated = await apiFetch<Job>(`/api/v1/jobs/${job.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setJob(updated);
      setLoadDecisionEditing(false);
      const freshAudit = await apiFetch<{ audit: AuditLogEntry[] }>(
        `/api/v1/jobs/${job.id}/audit`,
      );
      setAudit(freshAudit.audit);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setLoadDecisionSubmitError(err.message);
      } else {
        setLoadDecisionSubmitError(String(err));
      }
    } finally {
      setLoadDecisionSubmitting(false);
    }
  };

  if (error) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back to List
          </Button>
          <h1>Reviewed Job</h1>
        </header>
        <Card>
          <p className="err">Failed to load job.</p>
          <p className="muted">{error}</p>
        </Card>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back to List
          </Button>
          <h1>Reviewed Job</h1>
        </header>
        <p className="muted">Loading reviewed job details…</p>
      </main>
    );
  }

  const snap = (job.customerSnapshot ?? {}) as {
    customer?: Record<string, unknown>;
    property?: Record<string, unknown>;
  };
  const customer = (snap.customer ?? {}) as Record<string, unknown>;
  const property = (snap.property ?? {}) as Record<string, unknown>;
  const address = [property.addressLine1, property.city, property.state, property.zip]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(", ");

  const isDeliveryOnly = job.jobType === "D_O";

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button variant="ghost" onClick={back}>
            ← Back to List
          </Button>
          <h1>Job {job.jobNumber}</h1>
          <StatusTag state={job.state} />
        </div>
      </header>

      {/* Warning Banners */}
      {job.photoSource === "GALLERY" && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          ⚠️ <strong>Photo from GALLERY</strong> — driver uploaded from camera roll, not live capture.
        </div>
      )}

      {job.queuedOffline && (
        <div className="banner banner-info" style={{ marginBottom: "16px" }}>
          📶 <strong>Queued offline</strong> — driver submitted completion while offline; synced later.
        </div>
      )}

      <div className="office-grid">
        {/* Left Column: Job Info, Photos, Load Decision */}
        <div className="office-col">
          <Card>
            <h2>Customer &amp; Site</h2>
            <dl className="detail-list">
              <div className="detail-row">
                <dt>Company</dt>
                <dd>{String(customer.companyName ?? "—")}</dd>
              </div>
              <div className="detail-row">
                <dt>Site Name</dt>
                <dd>{String(property.siteName ?? "—")}</dd>
              </div>
              <div className="detail-row">
                <dt>Site Address</dt>
                <dd>{address || "—"}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h2>Job Specifications</h2>
            <dl className="detail-list">
              <div className="detail-row">
                <dt>Job Type</dt>
                <dd>{JOB_TYPE_LABELS[job.jobType]}</dd>
              </div>
              <div className="detail-row">
                <dt>Dumpster Size</dt>
                <dd>{job.dumpsterSize ? `${job.dumpsterSize.label} (${job.dumpsterSize.yards} yd)` : "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Truck</dt>
                <dd>{job.truck ? `Truck #${job.truck.unitNumber}` : "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Driver</dt>
                <dd>{job.driver?.name ?? "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Landfill</dt>
                <dd>{job.landfill?.name ?? "—"}</dd>
              </div>
            </dl>
          </Card>

          <TicketPhotoCard jobId={id} ticketPhotoUrl={job.ticketPhotoUrl} />

          {/* Unit #26: LOAD PHOTOS section */}
          <LoadPhotosSection jobId={id} />

          {/* Unit #26: LOAD DECISION panel */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2>Load Decision</h2>
              {!loadDecisionEditing && (
                <Button variant="secondary" onClick={() => {
                  setLoadDecisionEditing(true);
                  setLoadDecision(job.loadDecision ?? "");
                  setLoadNote(job.loadNote ?? "");
                }}>
                  {job.loadDecision ? "Edit" : "Set Decision"}
                </Button>
              )}
            </div>

            {loadDecisionSubmitError && (
              <p className="form-error err" style={{ marginBottom: "12px" }}>{loadDecisionSubmitError}</p>
            )}

            {!loadDecisionEditing ? (
              <div>
                {job.loadDecision ? (
                  <>
                    <div
                      className={job.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
                      style={{ marginBottom: "8px", display: "inline-block" }}
                    >
                      {job.loadDecision === "LANDFILL" ? "Load Decision: LANDFILL" : "Load Decision: FACILITY METAL"}
                    </div>
                    {job.loadNote && <p className="muted">{job.loadNote}</p>}
                  </>
                ) : (
                  <p className="muted">No load decision set.</p>
                )}
              </div>
            ) : (
              <form onSubmit={handleLoadDecisionSubmit} className="form-stack">
                <div className="form-group">
                  <label>Decision</label>
                  <select
                    value={loadDecision}
                    onChange={(e) => setLoadDecision(e.target.value as LoadDecision | "")}
                  >
                    <option value="">— Select —</option>
                    <option value="LANDFILL">Landfill</option>
                    <option value="FACILITY_METAL">Facility (Pull Metal)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Note (optional)</label>
                  <textarea
                    rows={2}
                    value={loadNote}
                    onChange={(e) => setLoadNote(e.target.value)}
                    placeholder="e.g. mixed metal visible, route to facility"
                    maxLength={2000}
                  />
                </div>
                <div className="form-group">
                  <label>Reason for Edit*</label>
                  <select
                    value={loadDecisionReason}
                    onChange={(e) => setLoadDecisionReason(e.target.value as EditReason)}
                    required
                  >
                    {Object.entries(EDIT_REASON_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <FieldError message={loadDecisionFieldErrors.reason} />
                </div>
                <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                  <Button type="submit" disabled={loadDecisionSubmitting}>
                    {loadDecisionSubmitting ? "Saving…" : "Save Decision"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setLoadDecisionEditing(false);
                      setLoadDecisionSubmitError(null);
                      setLoadDecisionFieldErrors({});
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>

        {/* Right Column: Reviewed Money & Audit Trail */}
        <div className="office-col">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2>Billing &amp; Money</h2>
              {!editing && (
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit Money
                </Button>
              )}
            </div>

            {!editing ? (
              <dl className="detail-list">
                {!isDeliveryOnly ? (
                  <>
                    <div className="detail-row">
                      <dt>Scale Weight</dt>
                      <dd>{job.weightTons !== null && job.weightTons !== undefined ? `${String(job.weightTons)} tons` : "—"}</dd>
                    </div>
                    <div className="detail-row">
                      <dt>Disposal Cost</dt>
                      <dd>{money(typeof job.disposalAmountCents === "number" ? job.disposalAmountCents : undefined)}</dd>
                    </div>
                  </>
                ) : (
                  <div className="banner banner-info" style={{ marginBottom: "12px" }}>
                    Delivery-only job (no disposal ticket).
                  </div>
                )}
                <div className="detail-row" style={{ fontSize: "16px", fontWeight: "bold" }}>
                  <dt>Billable Amount</dt>
                  <dd style={{ color: "var(--accent)" }}>
                    {money(typeof job.billableAmountCents === "number" ? job.billableAmountCents : undefined)}
                  </dd>
                </div>
              </dl>
            ) : (
              <form onSubmit={handleEditSubmit} className="form-stack">
                {submitError && (
                  <div className="banner banner-danger" style={{ marginBottom: "12px" }}>
                    {submitError}
                  </div>
                )}

                {isDeliveryOnly ? (
                  <div className="banner banner-info" style={{ marginBottom: "12px" }}>
                    Delivery-only job: weight and disposal cannot be set. Only billable amount is editable.
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="editWeight">Weight (Tons)</label>
                      <input
                        id="editWeight"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={weightTons}
                        onChange={(e) => setWeightTons(e.target.value)}
                      />
                      <FieldError message={fieldErrors.weightTons} />
                    </div>

                    <div className="form-group">
                      <label htmlFor="editDisposal">Disposal Cost ($)</label>
                      <input
                        id="editDisposal"
                        type="number"
                        step="0.01"
                        min="0"
                        maxLength={12}
                        value={disposalAmount}
                        onChange={(e) => setDisposalAmount(e.target.value)}
                      />
                      <FieldError message={fieldErrors.disposalAmount} />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="editBillable">Billable Amount ($)</label>
                  <input
                    id="editBillable"
                    type="number"
                    step="0.01"
                    min="0"
                    maxLength={12}
                    value={billableAmount}
                    onChange={(e) => setBillableAmount(e.target.value)}
                    required
                  />
                  <FieldError message={fieldErrors.billableAmount} />
                </div>

                <div className="form-group">
                  <label htmlFor="editReason">Reason for Edit*</label>
                  <select
                    id="editReason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value as EditReason)}
                    required
                  >
                    {EditReason.options.map((r) => (
                      <option key={r} value={r}>
                        {EDIT_REASON_LABELS[r]}
                      </option>
                    ))}
                  </select>
                  <FieldError message={fieldErrors.reason} />
                </div>

                <div className="form-group">
                  <label htmlFor="editNote">Note (optional)</label>
                  <textarea
                    id="editNote"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Explanation for audit log…"
                  />
                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : "Save Changes"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setSubmitError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </Card>

          {/* Audit Trail */}
          <Card>
            <h2>Audit History</h2>
            {audit.length === 0 ? (
              <p className="muted">No audit history recorded.</p>
            ) : (
              <div className="audit-timeline">
                {audit.map((entry) => {
                  const b = entry.before as Record<string, unknown> | null;
                  const a = entry.after as Record<string, unknown> | null;
                  return (
                    <div key={entry.id} className="audit-entry">
                      <div className="audit-entry-header">
                        <span className="audit-action-tag">
                          {entry.action === "REVIEW" ? "Initial Review" : "Edited"}
                        </span>
                        <span className="audit-time">{formatDateTime(entry.at)}</span>
                      </div>
                      {entry.reason && (
                        <p className="audit-reason">
                          <strong>Reason:</strong> {EDIT_REASON_LABELS[entry.reason as EditReason] || entry.reason}
                        </p>
                      )}

                      <div className="audit-diff">
                        {entry.action === "REVIEW" ? (
                          <div className="audit-diff-row">
                            <span>Recorded:</span>
                            <span>
                              {a?.weightTons !== undefined ? `${String(a.weightTons)} tons · ` : ""}
                              {a?.disposalAmountCents !== undefined
                                ? `Disposal: ${money(typeof a.disposalAmountCents === "number" ? a.disposalAmountCents : undefined)} · `
                                : ""}
                              {a?.billableAmountCents !== undefined
                                ? `Billable: ${money(typeof a.billableAmountCents === "number" ? a.billableAmountCents : undefined)}`
                                : ""}
                              {a?.loadDecision !== undefined && a.loadDecision !== null
                                ? ` · Load: ${String(a.loadDecision)}`
                                : ""}
                            </span>
                          </div>
                        ) : (
                          <>
                            {b?.weightTons !== a?.weightTons && (
                              <div className="audit-diff-row">
                                <span>Weight:</span>
                                <span>
                                  <del>{b?.weightTons !== undefined ? String(b.weightTons) : "—"}</del> → <strong>{a?.weightTons !== undefined ? String(a.weightTons) : "—"}</strong> tons
                                </span>
                              </div>
                            )}
                            {b?.disposalAmountCents !== a?.disposalAmountCents && (
                              <div className="audit-diff-row">
                                <span>Disposal:</span>
                                <span>
                                  <del>{money(typeof b?.disposalAmountCents === "number" ? b.disposalAmountCents : undefined)}</del> → <strong>{money(typeof a?.disposalAmountCents === "number" ? a.disposalAmountCents : undefined)}</strong>
                                </span>
                              </div>
                            )}
                            {b?.billableAmountCents !== a?.billableAmountCents && (
                              <div className="audit-diff-row">
                                <span>Billable:</span>
                                <span>
                                  <del>{money(typeof b?.billableAmountCents === "number" ? b.billableAmountCents : undefined)}</del> → <strong>{money(typeof a?.billableAmountCents === "number" ? a.billableAmountCents : undefined)}</strong>
                                </span>
                              </div>
                            )}
                            {b?.loadDecision !== a?.loadDecision && (
                              <div className="audit-diff-row">
                                <span>Load Decision:</span>
                                <span>
                                  <del>{b?.loadDecision !== null && b?.loadDecision !== undefined ? String(b.loadDecision) : "—"}</del> → <strong>{a?.loadDecision !== null && a?.loadDecision !== undefined ? String(a.loadDecision) : "—"}</strong>
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <details className="audit-raw-details">
                        <summary>Raw JSON</summary>
                        <pre>{JSON.stringify({ before: b, after: a }, null, 2)}</pre>
                      </details>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </main>
  );
}
